import { FC, ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'

import { Block, Edit } from '@mui/icons-material'
import { Box, Button, Card, CardContent, CardHeader, Chip, Divider, FormControlLabel, Grid, Skeleton, Stack, Switch, TextField } from '@mui/material'

import { useError } from '../../../hooks/useError'
import usePage from '../../../hooks/usePage'
import PageService from '../../../services/PageService'
import { BackButton } from '../../components/BackButton/BackButton'
import FloatingToolbar from '../../components/FloatingToolbar/FloatingToolbar'
import { MarketBlueprintDataIssuesCard } from '../../features/live-events-tracking/components/MarketBlueprintDataIssuesCard/MarketBlueprintDataIssuesCard'
import { MarketBlueprintOddCasesCard } from '../../features/live-events-tracking/components/MarketBlueprintOddCasesCard/MarketBlueprintOddCasesCard'
import { MarketBlueprintOddTypesCard } from '../../features/live-events-tracking/components/MarketBlueprintOddTypesCard/MarketBlueprintOddTypesCard'
import { MarketLevelBlueprintGamesTable } from '../../features/live-events-tracking/components/MarketLevelBlueprintGamesTable/MarketLevelBlueprintGamesTable'
import { MarketLevelBlueprintsOverviewTable } from '../../features/live-events-tracking/components/MarketLevelBlueprintsOverviewTable/MarketLevelBlueprintsOverviewTable'
import { MarketBlueprintUsage, useMarketBlueprintUsage } from '../../features/live-events-tracking/hooks/useMarketBlueprintUsage'

type MarketLevelBlueprintsOverviewPageProps = {
  children?: ReactNode
}

/**
 * Internal data sanity check page for Market Level Blueprints.
 *
 * Without an archetype id in the URL the page lists all Market Level Blueprints
 * with usage counts and data quality issues. With an archetype id it shows the
 * games (and their game-level blueprints) tagged to the selected blueprint.
 */
export const MarketLevelBlueprintsOverviewPage: FC<MarketLevelBlueprintsOverviewPageProps> = ({ children }) => {
  usePage(PageService.getInternalPageWithId('internal-market-level-blueprints-overview'))
  const { archetypeId } = useParams<{ archetypeId?: string }>()
  const { usages, usageById, dataIssues, isLoading, error } = useMarketBlueprintUsage()
  useError({ error })

  const selectedUsage = archetypeId ? usageById[archetypeId] : undefined

  return (
    <>
      {!archetypeId && <BlueprintsListView usages={usages} dataIssues={dataIssues} isLoading={isLoading} />}
      {archetypeId && <BlueprintDetailView usage={selectedUsage} isLoading={isLoading} />}

      <FloatingToolbar>
        <BackButton
          mb={0}
          url={archetypeId ? '/internal/live-events-tracking/market-level-blueprints/overview' : '/internal/live-events-tracking'}
          viewTransition
        />
      </FloatingToolbar>
    </>
  )
}

const BlueprintsListView: FC<{
  usages: MarketBlueprintUsage[]
  dataIssues: ReturnType<typeof useMarketBlueprintUsage>['dataIssues']
  isLoading: boolean
}> = ({ usages, dataIssues, isLoading }) => {
  const { t } = useTranslation()
  const [searchText, setSearchText] = useState('')
  const [showDiscontinued, setShowDiscontinued] = useState(true)

  const filteredUsages = useMemo(() => {
    const search = searchText.trim().toLowerCase()
    return usages
      .filter((usage) => showDiscontinued || !usage.marketArchetype.discontinued)
      .filter((usage) => !search || (usage.marketArchetype.names.en || '').toLowerCase().includes(search))
  }, [usages, searchText, showDiscontinued])

  return (
    <>
      <Card sx={{ marginTop: 3 }}>
        <CardHeader title={t('internal-live-events:market_level_blueprints_overview')} />
        <Divider />
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs>
              <TextField
                type="text"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                variant="outlined"
                size="small"
                label={t('internal-live-events:search_market_level_blueprints')}
                fullWidth
              />
            </Grid>
            <Grid item>
              <FormControlLabel
                control={<Switch checked={showDiscontinued} onChange={(event) => setShowDiscontinued(event.target.checked)} />}
                label={t('internal-live-events:show_discontinued_blueprints')}
              />
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <CardContent sx={{ padding: 0, '&:last-child': { paddingBottom: 0 } }}>
          <MarketLevelBlueprintsOverviewTable usages={filteredUsages} isLoading={isLoading} />
        </CardContent>
      </Card>

      {!isLoading && <MarketBlueprintOddTypesCard marketArchetypes={usages.map((usage) => usage.marketArchetype)} />}

      {!isLoading && <MarketBlueprintDataIssuesCard dataIssues={dataIssues} />}
    </>
  )
}

const BlueprintDetailView: FC<{ usage?: MarketBlueprintUsage; isLoading: boolean }> = ({ usage, isLoading }) => {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <Card sx={{ marginTop: 3 }}>
        <CardContent>
          <Skeleton variant="text" width={300} height={40} />
          <Skeleton variant="rounded" height={120} sx={{ marginTop: 2 }} />
        </CardContent>
      </Card>
    )
  }

  if (!usage) {
    return (
      <Card sx={{ marginTop: 3 }}>
        <CardContent>{t('internal-live-events:no_market_level_blueprints_found')}</CardContent>
      </Card>
    )
  }

  const descriptionHtml = usage.marketArchetype.descriptions?.en || ''

  return (
    <>
      <Card sx={{ marginTop: 3 }}>
        <CardHeader
          title={
            <Stack direction="row" spacing={1} alignItems="center">
              <span>{usage.marketArchetype.names.en}</span>
              {usage.marketArchetype.discontinued && (
                <Chip size="small" color="error" icon={<Block />} sx={{ margin: 0 }} label={t('live-events:live_event_evolution_type_discontinued')} />
              )}
            </Stack>
          }
          action={
            <Button
              component={Link}
              to={`/internal/live-events-tracking/market-level-blueprints/edit/${usage.marketArchetype.id}`}
              startIcon={<Edit />}
              variant="outlined"
            >
              {t('internal-live-events:edit_market_level_blueprint')}
            </Button>
          }
        />
        <Divider />
        <CardContent>
          <Stack direction="row" spacing={1} sx={{ marginBottom: descriptionHtml ? 2 : 0 }}>
            <Chip size="small" sx={{ margin: 0 }} label={t('internal-live-events:linked_games_count', { count: usage.games.length })} />
            <Chip size="small" sx={{ margin: 0 }} label={t('internal-live-events:linked_game_blueprints_count', { count: usage.gameBlueprintCount })} />
          </Stack>
          {descriptionHtml && <Box dangerouslySetInnerHTML={{ __html: descriptionHtml }} />}
        </CardContent>
      </Card>

      <MarketBlueprintOddCasesCard usage={usage} />

      <Card sx={{ marginTop: 3 }}>
        <CardHeader title={t('internal-live-events:games_using_market_level_blueprint')} />
        <Divider />
        <CardContent sx={{ padding: 0, '&:last-child': { paddingBottom: 0 } }}>
          <MarketLevelBlueprintGamesTable usage={usage} />
        </CardContent>
      </Card>
    </>
  )
}
